import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import EntityLink from './EntityLink';
import { useField } from 'react-final-form';

/* eslint-disable @typescript-eslint/no-explicit-any -- options are API entities */
type Option = { id: number; [key: string]: any };

export interface RefPickerFieldProps {
  /** react-final-form field name; the value is the picked entity (or null). */
  name: string;
  label: string;
  options: Option[] | undefined;
  /** The property shown for each option. */
  textField?: string;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  size?: 'small' | 'medium';
  /** Hides the label visually (a table column header labels it) but keeps it for assistive tech. */
  hideLabel?: boolean;
  /** The picked entity's own page; shows the looking glass beside the field once something is picked. */
  linkTo?: (option: Option) => string;
}

/** An entity picker bound to a final-form field, showing validation once the field is touched. */
export function RefPickerField({
  name,
  label,
  options,
  textField = 'name',
  loading,
  disabled,
  placeholder,
  helperText,
  size = 'small',
  hideLabel = false,
  linkTo,
}: RefPickerFieldProps) {
  const { input, meta } = useField<Option | null>(name, { subscription: { value: true, error: true, touched: true } });
  const showError = Boolean(meta.touched && meta.error);
  // A value saved as a bare `{ id }` takes its name from the matching option.
  const value = input.value ? (options?.find((option) => option.id === input.value?.id) ?? input.value) : null;
  const picker = (
    <Autocomplete<Option, false, false, false>
      options={options ?? []}
      value={value}
      loading={loading}
      disabled={disabled}
      autoHighlight
      size={size}
      getOptionLabel={(option) => String(option?.[textField] ?? '')}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_event, next) => input.onChange(next ?? null)}
      onBlur={() => input.onBlur()}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hideLabel ? undefined : label}
          placeholder={placeholder}
          error={showError}
          helperText={showError ? meta.error : helperText}
          slotProps={{ ...params.slotProps, htmlInput: { ...params.slotProps.htmlInput, 'aria-label': label } }}
        />
      )}
      sx={{ flex: 1, minWidth: 0 }}
    />
  );
  if (!linkTo) return picker;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, minWidth: 0 }}>
      {/* A fixed slot ahead of the field, so fields line up whether or not a value is picked. */}
      <Box sx={{ width: 34, pt: 0.5, flex: '0 0 auto' }}>
        {value?.id != null && (
          <EntityLink
            to={linkTo(value)}
            label={`Open ${label.toLowerCase()} ${value[textField] ?? value.id} in a new tab`}
          />
        )}
      </Box>
      {picker}
    </Box>
  );
}

/** A text or number input bound to a final-form field. */
export function FormTextField({
  name,
  label,
  type = 'text',
  multiline,
  minRows,
  placeholder,
  disabled,
  parse,
}: {
  name: string;
  label: string;
  type?: string;
  multiline?: boolean;
  minRows?: number;
  placeholder?: string;
  disabled?: boolean;
  parse?: (value: string) => unknown;
}) {
  const { input, meta } = useField(name, {
    parse: parse as any,
    subscription: { value: true, error: true, touched: true },
  });
  const showError = Boolean(meta.touched && meta.error);
  return (
    <TextField
      {...input}
      value={input.value ?? ''}
      type={type}
      label={label}
      size='small'
      fullWidth
      multiline={multiline}
      minRows={minRows}
      placeholder={placeholder}
      disabled={disabled}
      error={showError}
      helperText={showError ? meta.error : undefined}
    />
  );
}
